/*
 * Manual send follows the server's durable hold/claim/result, never a browser timer. Only status
 * reads poll; a failed send request is never repeated and an unknown outcome keeps Send locked.
 */
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  InboxSendRequest,
  InboxSendStatus,
  SendSettlementRequest,
} from "../../src/inbox-v2/contract.ts";
import type { InboxDraftSendRequest } from "../../src/inbox-v2/draft-send-contract.ts";
import { OFFER_QUERY } from "../draft-again.tsx";
import { InboxApiError, inboxApi } from "./api.ts";
import { refreshAfter } from "./effects.ts";

function refusal(error: InboxApiError): string {
  if (error.code === "stale_revision")
    return "The conversation changed. Review the latest messages, then press Send again.";
  if (error.code === "original_file_duplicate")
    return "Email Bison already attaches the original email's files. Remove your copy, then press Send again.";
  return error.code
    ? `The request was refused (${error.code.replaceAll("_", " ")}).`
    : "The request was refused. Check the conversation and workspace settings.";
}

type Write = "send" | "undo" | "settle";

const UNCONFIRMED: Record<Write, string> = {
  send: "Sending could not be confirmed. Check Bison before taking further action.",
  undo: "Undo could not be confirmed. Checking the send status.",
  settle: "Your answer could not be confirmed. Checking the send status.",
};

/** Only a send that failed can have gone out unseen; an undo or an answer sends nothing. */
function writeFailure(cause: unknown, write: Write): { unknown: boolean; text: string } {
  if (cause instanceof InboxApiError && cause.status < 500)
    return { unknown: false, text: refusal(cause) };
  return { unknown: write === "send", text: UNCONFIRMED[write] };
}

type Unknown = { previousSendId: string | null; operationId?: string } | null;

function unresolvedSend(unknown: Unknown, current: InboxSendStatus["send"] | undefined): boolean {
  if (!unknown) return false;
  return unknown.operationId
    ? current?.id !== unknown.operationId
    : !current || current.id === unknown.previousSendId;
}

/*
 * An unknown outcome outlives the box that saw it. The reply box is drawn again for every
 * conversation the rep opens (J, K, a click), and a box drawn afresh must not take an unresolved
 * send for none and offer Send again. The conversation keeps it until the attempted operation is
 * found; a status read for a different parent cannot settle it. Earlier clients name only the prior send.
 */
const unknownSends = new Map<string, NonNullable<Unknown>>();

function useUnknown(id: string): [Unknown, (value: Unknown) => void] {
  // The record is the map's; the state only redraws.
  const [, redraw] = useState(0);
  const set = useCallback(
    (value: Unknown) => {
      if (value) unknownSends.set(id, value);
      else unknownSends.delete(id);
      redraw((n) => n + 1);
    },
    [id],
  );
  return [unknownSends.get(id) ?? null, set];
}

/**
 * One message's send status: its key and its read. The thread reads a reply's status in flight by
 * the same key (`outgoing-reply.ts`), without polling it a second time.
 */
export function sendStatusQuery(
  workspaceId: string,
  planId: string,
  messageId: string | null,
  mode: "reply" | "forward" = "reply",
  selectedReply = false,
) {
  return queryOptions({
    queryKey:
      mode === "reply"
        ? (["inbox-v2", workspaceId, "reply-send", planId, messageId] as const)
        : (["inbox-v2", workspaceId, "reply-send", planId, messageId, "forward"] as const),
    queryFn: ({ signal }) =>
      inboxApi.sendStatus(
        workspaceId,
        planId,
        signal,
        mode === "forward" || selectedReply ? (messageId ?? undefined) : undefined,
      ),
  });
}

export function useReplySend(
  workspaceId: string,
  planId: string,
  messageId: string | null,
  mode: "reply" | "forward" = "reply",
  afterSendId: string | null = null,
  selectedReply = false,
) {
  const client = useQueryClient();
  const read = sendStatusQuery(workspaceId, planId, messageId, mode, selectedReply);
  const key = read.queryKey;
  const [busy, setBusy] = useState(false);
  const [unknown, setUnknown] = useUnknown([workspaceId, planId].join("\u0000"));
  const [error, setError] = useState<string | null>(null);
  const writing = useRef(false);
  const refreshed = useRef<string | null>(null);
  const status = useQuery({
    ...read,
    refetchOnMount: "always",
    refetchInterval: (query) => {
      const current = query.state.data?.send;
      const unresolved = unresolvedSend(unknown, current);
      return unresolved || current?.state === "queued" || current?.state === "claimed"
        ? 1_000
        : false;
    },
  });
  const current = status.data?.send;
  const terminal = current && ["sent", "cancelled", "refused"].includes(current.state);
  const send =
    terminal && (!!current.forwardTo !== (mode === "forward") || current.id === afterSendId)
      ? null
      : current;
  const unresolved = unresolvedSend(unknown, current);
  useEffect(() => {
    if (!unknown || !current || unresolvedSend(unknown, current)) return;
    setUnknown(null);
    setError(null);
  }, [unknown, current, setUnknown]);
  useEffect(() => {
    if (send?.state !== "sent" || refreshed.current === send.id) return;
    refreshed.current = send.id;
    // A sent reply changes this conversation's thread, the lists, counts and badge, and the agent's
    // reading of it; not saved views, suggestions or import progress (W5, `effects.ts`).
    void refreshAfter(client, workspaceId, "sent", [planId]);
  }, [client, workspaceId, planId, send?.id, send?.state]);
  // Replies unlock after confirmation; forwards keep their receipt until "Forward again".
  const held =
    send != null &&
    (["queued", "claimed", "uncertain"].includes(send.state) ||
      (mode === "forward" && send.state === "sent"));
  const locked =
    busy || unresolved || !status.data || status.isFetching || status.isError || !messageId || held;

  const recoverWrite = async (cause: unknown, kind: Write, operationId?: string) => {
    const failure = writeFailure(cause, kind);
    if (failure.unknown)
      setUnknown({ previousSendId: current?.id ?? null, ...(operationId ? { operationId } : {}) });
    setError(failure.text);
    if (cause instanceof InboxApiError && cause.code === "stale_revision")
      await refreshAfter(client, workspaceId, "arrival", [planId]);
    await status.refetch();
  };

  const write = async (
    operation: () => Promise<InboxSendStatus>,
    kind: Write,
    operationId?: string,
  ) => {
    if (writing.current) return;
    writing.current = true;
    setBusy(true);
    setError(null);
    try {
      await client.cancelQueries({ queryKey: key, exact: true });
      client.setQueryData(key, await operation());
      setUnknown(null);
    } catch (cause) {
      await recoverWrite(cause, kind, operationId);
    } finally {
      writing.current = false;
      setBusy(false);
    }
  };

  return {
    status: status.data ? { ...status.data, send: send ?? null } : undefined,
    loading: status.isPending,
    statusFailed: status.isError,
    locked,
    held,
    busy,
    unknown: unresolved,
    error,
    check: () => void status.refetch(),
    send: (request: InboxSendRequest | InboxDraftSendRequest) => {
      if (locked || !!request.forwardTo !== (mode === "forward")) return;
      const result = write(
        () =>
          request.forwardTo
            ? inboxApi.forward(workspaceId, planId, request)
            : inboxApi.send(workspaceId, planId, request),
        "send",
        request.reply ? request.operationId : undefined,
      );
      if ("draft" in request)
        void result.then(() =>
          Promise.all([
            refreshAfter(client, workspaceId, "arrival", [planId]),
            client.invalidateQueries({ queryKey: [OFFER_QUERY, planId] }),
          ]),
        );
      return result;
    },
    undo: () => {
      if (send?.state !== "queued") return;
      return write(() => inboxApi.undoSend(workspaceId, planId, send.id), "undo");
    },
    /** A person's answer once LeadCue has stopped looking for an unknown send; it sends nothing. */
    settle: (action: SendSettlementRequest["action"], candidateAt?: string) => {
      if (send?.state !== "uncertain" || !send.check?.stopped) return;
      return write(
        () =>
          inboxApi.settleSend(workspaceId, planId, {
            id: send.id,
            action,
            ...(candidateAt ? { candidateAt } : {}),
          }),
        "settle",
      );
    },
  };
}
